/**
 * Roteiros do modo Treino (item 5.2): o tutorial e os desafios por posição mexem no mundo da simulação
 * (posições, bola, Petardos), conferem o progresso depois de cada tick e mostram marcadores no campo.
 * Não dependem de three nem do navegador: dá para rodar e testar sem desenhar nada.
 */
import type { InputCommand, PlayEvent, Simulation, Vec3, WorldState } from "@besenliga/sim";

/** Acesso ao mundo: ler o estado e trocar o que for preciso entre dois ticks. */
export interface WorldAccess {
  readonly state: Readonly<WorldState>;
  edit(fn: (w: WorldState) => void): void;
}

/** Marcador no campo: aro de passagem (o próximo é destacado) ou indicador sobre um atleta. */
export interface Marker {
  position: Vec3;
  radius: number;
  state: "proximo" | "depois" | "feito";
}

/** O que a tela mostra do roteiro. */
export interface ScenarioHud {
  title: string;
  text: string;
  /** "Etapa 3 de 8", "Gols: 4". */
  progress: string;
  /** Segundos que restam (desafio) ou passados (tutorial). */
  seconds: number;
  countdown: boolean;
  done: boolean;
  /** Resumo do fim (tempo do tutorial, pontos do desafio). */
  summary?: string;
  /** Pontos do desafio. */
  score?: number;
}

export interface Scenario {
  start(world: WorldAccess): void;
  /** Comandos de atletas roteirizados, por cima dos bots (ex.: o arremessador do Paredão). */
  inputs?(w: Readonly<WorldState>): Record<string, InputCommand>;
  afterStep(world: WorldAccess, plays: readonly PlayEvent[]): void;
  markers(w: Readonly<WorldState>): Marker[];
  hud(): ScenarioHud;
  /** Pula a etapa atual (tutorial). */
  skip?(world: WorldAccess): void;
}

/** Acesso ao mundo em cima de uma `Simulation` (o cliente e os testes usam o mesmo). */
export function simAccess(
  get: () => Simulation,
  set: (s: Simulation) => void,
  make: (w: WorldState) => Simulation,
): WorldAccess {
  return {
    get state() {
      return get().state;
    },
    edit(fn) {
      const w = get().snapshot();
      fn(w);
      set(make(w));
    },
  };
}

export const flatDist = (a: Vec3, b: Vec3) =>
  Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));
export const dist3 = (a: Vec3, b: Vec3) =>
  Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y) + (a.z - b.z) * (a.z - b.z));

/** Deixa os Petardos parados num canto, a Faísca fora de campo e a bola longe (para lições sem distração). */
export function calmWorld(w: WorldState): void {
  if (!w.balls) return;
  w.balls.petardos.forEach((p, i) => {
    p.position = { x: (i === 0 ? -1 : 1) * 20, y: 38, z: 44 };
    p.velocity = { x: 0, y: 0, z: 0 };
    p.mode = "descanso";
    p.modeTicks = 1_000_000;
  });
  w.balls.faisca.active = false;
  w.balls.faisca.appearTick = 2_000_000_000;
}
